import {
  ApiError,
  createApiClient,
  type ApiClient,
  type ApiClientOptions,
  type Method,
  type RequestOptions,
} from './client';
import { createRefreshStore, REFRESH_KEY, type RefreshStore } from './refreshStore';

/**
 * The session: sign in, hold it, refresh it, sign out. Unit F4, held as D6
 * rules for X1: the access token in memory, the refresh token in storage.
 *
 * Shapes are read off `veri-plus-backend` at `042f3b7`,
 * `src/controllers/auth.controller.ts`. Login answers either the pair with the
 * user, or `{ challengeId }` when an OTP is owed. Verify-OTP and refresh answer
 * the pair with the user. Logout takes the bearer and answers 204.
 *
 * **Refresh rotates.** `auth.service.ts` `refresh` swaps the stored hash, so a
 * refresh token works exactly once. Two refreshes in flight from one tab would
 * spend it twice and the second would sign the user out, so a tab has at most
 * one refresh in flight and every caller shares it. Another tab can still
 * spend it first; when a refusal comes back and storage holds a different
 * token than the one sent, that tab rotated it, and the new one is tried once.
 *
 * **Only `invalid_refresh_token` signs out.** A network failure, a 500 or a
 * body that is not the pair says nothing about the token, so it is kept and
 * the error is thrown to the caller. Signing a user out because the train went
 * into a tunnel is the mistake `veriplus-mobile-app` `refreshSession.ts`
 * records.
 *
 * **Only a tenant is admitted**, D20. A pair for anyone the backend's own org
 * gate would refuse (`requireOrgContext.ts:18`) is revoked on its own bearer,
 * nothing is stored, and the state stays `signed_out`, so the shell never
 * renders for a consumer. Login and verify-OTP throw `not_a_tenant`; a refresh
 * that answers one ends the session as a refused token would.
 *
 * **A sign-out in one tab signs out the others**, H6. Every tab shares the one
 * stored refresh token, so a tab that sees it removed by another tab drops its
 * own access token and signs out at once rather than on its next refresh. A
 * token written by another tab is a rotation or a sign-in there and changes
 * nothing here.
 */

/** The user as `User.toJSON()` returns it, `src/models/user.model.ts`. Only the fields read here. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
  organizationId?: string;
}

export type SessionState =
  | { status: 'restoring' }
  | { status: 'signed_out' }
  /** A refresh token is held but the API gave no answer about it. It is kept. */
  | { status: 'unreachable' }
  | { status: 'signed_in'; user: SessionUser };

export type SignInResult = { kind: 'signed_in' } | { kind: 'challenge'; challengeId: string };

/**
 * A session held only while an invitation is redeemed, L1. Whoever joins is a
 * consumer until the invitation is spent, and a consumer's pair is never the
 * portal's session, so it is kept here, in memory, and never stored. Redeeming
 * rotates it, which is why it is a mutable object rather than a value.
 */
export interface JoinTicket {
  accessToken: string;
  refreshToken: string;
}

export type JoinStart =
  { kind: 'ticket'; ticket: JoinTicket } | { kind: 'challenge'; challengeId: string };

export type RedeemResult = { kind: 'joined' } | { kind: 'challenge'; challengeId: string };

export interface Session {
  /** The client every screen uses. A 401 refreshes once and retries once. */
  api: ApiClient;
  getState(): SessionState;
  subscribe(listener: () => void): () => void;
  /** On load: spend the stored refresh token for an access token and the user. */
  restore(): Promise<void>;
  signIn(email: string, password: string): Promise<SignInResult>;
  verifyOtp(challengeId: string, code: string): Promise<void>;
  /** Emails a fresh code for the same challenge. The API refuses inside its 60 second cooldown. */
  resendOtp(challengeId: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Asks for a password reset code by email. The API answers 204 whether or
   * not the address has an account, so this resolves the same either way.
   */
  forgotPassword(email: string): Promise<void>;
  /** Spends a reset code for a new password. The API revokes every session of that user. */
  resetPassword(code: string, password: string): Promise<void>;
  /** Creates an unverified consumer account. The API mails a code and answers its challenge. */
  register(email: string, password: string, name: string): Promise<string>;
  /** Signs in to join: the pair is a ticket whatever the role, or a code is owed first. */
  joinSignIn(email: string, password: string): Promise<JoinStart>;
  /** Spends a register or login code for a ticket rather than a session. */
  joinVerify(challengeId: string, code: string): Promise<JoinTicket>;
  /**
   * `POST /v1/invitations/accept` on the ticket. Without `answer` the API mails
   * a code and this resolves its challenge; with the answered code it joins,
   * spends the ticket's refresh token for the tenant's pair, and the session
   * is signed in.
   */
  redeem(
    ticket: JoinTicket,
    code: string,
    answer?: { challengeId: string; code: string },
  ): Promise<RedeemResult>;
  /** Revokes a ticket that will not be redeemed. Best effort, as sign-out is. */
  dropTicket(ticket: JoinTicket): Promise<void>;
  /** Listens for another tab's sign-out on `target`. Returns the unsubscribe. */
  followOtherTabs(target?: Pick<Window, 'addEventListener' | 'removeEventListener'>): () => void;
}

export interface SessionOptions extends Omit<ApiClientOptions, 'getToken'> {
  store?: RefreshStore;
}

export function createSession(options: SessionOptions = {}): Session {
  const { store = createRefreshStore(), ...clientOptions } = options;
  let access: string | null = null;
  let state: SessionState = { status: 'restoring' };
  const listeners = new Set<() => void>();
  let inflight: Promise<boolean> | null = null;

  const anon = createApiClient(clientOptions);
  const authed = createApiClient({ ...clientOptions, getToken: () => access });

  function set(next: SessionState) {
    state = next;
    for (const l of listeners) l();
  }

  async function adopt(body: unknown) {
    const pair = readPair(body);
    if (!isTenant(pair.user)) {
      await revoke(pair.accessToken);
      end();
      throw new ApiError(403, 'not_a_tenant', 'This account does not belong to a brand.');
    }
    access = pair.accessToken;
    store.write(pair.refreshToken);
    set({ status: 'signed_in', user: pair.user });
  }

  /** Best effort, as sign-out is: the pair is dropped here whatever the answer. */
  async function revoke(token: string) {
    try {
      await createApiClient({ ...clientOptions, getToken: () => token }).post('/auth/logout');
    } catch {
      // Nothing is stored, so an unrevoked session is only the server's to expire.
    }
  }

  function end() {
    access = null;
    store.clear();
    set({ status: 'signed_out' });
  }

  /** True when a fresh access token is held, false when the session is over. Throws when unanswered. */
  function refresh(): Promise<boolean> {
    inflight ??= spend().finally(() => {
      inflight = null;
    });
    return inflight;
  }

  async function spend(): Promise<boolean> {
    let sent = store.read();
    // Two attempts at most: the stored token, then one another tab left behind.
    for (let attempt = 0; attempt < 2 && sent !== null; attempt++) {
      try {
        await adopt(await anon.post<unknown>('/auth/refresh', { refreshToken: sent }));
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'not_a_tenant') return false;
        if (!(err instanceof ApiError) || err.code !== 'invalid_refresh_token') throw err;
        const now = store.read();
        if (now === sent) break;
        sent = now;
      }
    }
    end();
    return false;
  }

  async function request<T>(method: Method, path: string, opts?: RequestOptions): Promise<T> {
    const used = access;
    try {
      return await authed.request<T>(method, path, opts);
    } catch (err) {
      if (!(err instanceof ApiError) || err.kind !== 'unauthenticated' || store.read() === null)
        throw err;
      // Another call already refreshed while this one was out: just retry.
      if (access === used && !(await refresh())) throw err;
      return authed.request<T>(method, path, opts);
    }
  }

  const api: ApiClient = {
    request,
    get: (path, opts) => request('GET', path, opts),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    delete: (path, opts) => request('DELETE', path, opts),
  };

  return {
    api,
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async restore() {
      if (state.status === 'signed_in') return;
      if (store.read() === null) return set({ status: 'signed_out' });
      set({ status: 'restoring' });
      try {
        await refresh();
      } catch {
        set({ status: 'unreachable' });
      }
    },
    async signIn(email, password) {
      const body = await anon.post<unknown>('/auth/login', { email, password });
      if (isChallenge(body)) return { kind: 'challenge', challengeId: body.challengeId };
      await adopt(body);
      return { kind: 'signed_in' };
    },
    async verifyOtp(challengeId, code) {
      await adopt(await anon.post<unknown>('/auth/verify-otp', { challengeId, code }));
    },
    async resendOtp(challengeId) {
      await anon.post('/auth/resend-otp', { challengeId });
    },
    async signOut() {
      try {
        // Revokes this session server side. Best effort: the local sign-out happens regardless.
        if (store.read() !== null) await api.post('/auth/logout');
      } catch {
        // An unreachable API must not keep a user signed in on this machine.
      } finally {
        end();
      }
    },
    async forgotPassword(email) {
      await anon.post('/auth/forgot-password', { email });
    },
    async resetPassword(code, password) {
      await anon.post('/auth/reset-password', { token: code.trim(), password });
    },
    async register(email, password, name) {
      const body = await anon.post<unknown>('/auth/register', { email, password, name });
      if (!isChallenge(body)) throw unrecognised();
      return body.challengeId;
    },
    async joinSignIn(email, password) {
      const body = await anon.post<unknown>('/auth/login', { email, password });
      if (isChallenge(body)) return { kind: 'challenge', challengeId: body.challengeId };
      return { kind: 'ticket', ticket: ticketOf(body) };
    },
    async joinVerify(challengeId, code) {
      return ticketOf(await anon.post<unknown>('/auth/verify-otp', { challengeId, code }));
    },
    async redeem(ticket, code, answer) {
      const accept = () =>
        createApiClient({ ...clientOptions, getToken: () => ticket.accessToken }).post<unknown>(
          '/v1/invitations/accept',
          { token: code.trim() },
          answer
            ? { headers: { 'X-Otp-Challenge': answer.challengeId, 'X-Otp-Code': answer.code } }
            : {},
        );
      let body: unknown;
      try {
        body = await accept();
      } catch (err) {
        // The access token lasts minutes and a person may take longer between codes.
        if (!(err instanceof ApiError) || err.kind !== 'unauthenticated') throw err;
        rotate(
          ticket,
          await anon.post<unknown>('/auth/refresh', { refreshToken: ticket.refreshToken }),
        );
        body = await accept();
      }
      if (isChallenge(body)) return { kind: 'challenge', challengeId: body.challengeId };
      if (!isObject(body) || !isObject(body.organization)) throw unrecognised();
      // The role changed server side; the refresh is what carries it into a pair.
      await adopt(await anon.post<unknown>('/auth/refresh', { refreshToken: ticket.refreshToken }));
      return { kind: 'joined' };
    },
    async dropTicket(ticket) {
      await revoke(ticket.accessToken);
    },
    followOtherTabs(target = window) {
      const onStorage = (event: StorageEvent) => {
        // A null key is a `clear()` of the whole storage area.
        if (event.key !== null && event.key !== REFRESH_KEY) return;
        if (store.read() !== null || state.status === 'signed_out') return;
        // The other tab revoked it server side; this one only forgets.
        access = null;
        set({ status: 'signed_out' });
      };
      target.addEventListener('storage', onStorage);
      return () => target.removeEventListener('storage', onStorage);
    },
  };
}

/** The backend's org gate, `requireOrgContext.ts:18`: an organization and a tenant role. */
export function isTenant(user: SessionUser): boolean {
  return (
    user.organizationId !== undefined && (user.role === 'org_member' || user.role === 'org_admin')
  );
}

function isChallenge(body: unknown): body is { challengeId: string } {
  return (
    isObject(body) &&
    typeof body.challengeId === 'string' &&
    body.challengeId !== '' &&
    !('accessToken' in body)
  );
}

function ticketOf(body: unknown): JoinTicket {
  const { accessToken, refreshToken } = readPair(body);
  return { accessToken, refreshToken };
}

function rotate(ticket: JoinTicket, body: unknown) {
  Object.assign(ticket, ticketOf(body));
}

function unrecognised(): ApiError {
  return new ApiError(200, 'unrecognised_response', 'The API answered without a session.');
}

function readPair(body: unknown): { accessToken: string; refreshToken: string; user: SessionUser } {
  if (
    isObject(body) &&
    nonEmpty(body.accessToken) &&
    nonEmpty(body.refreshToken) &&
    isObject(body.user) &&
    nonEmpty(body.user.id) &&
    typeof body.user.email === 'string' &&
    typeof body.user.name === 'string' &&
    typeof body.user.role === 'string'
  ) {
    const { id, email, name, role, organizationId } = body.user;
    const user: SessionUser = { id, email, name, role };
    if (typeof organizationId === 'string') user.organizationId = organizationId;
    return { accessToken: body.accessToken, refreshToken: body.refreshToken, user };
  }
  // A 200 that is not the pair. Nothing about the token is known, so it is not an auth failure.
  throw unrecognised();
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}
